import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import CookieConsent from './components/CookieConsent';
import Navbar from './components/common/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import IdeaGenerator from './pages/IdeaGenerator';
import IdeaChoice from './pages/IdeaChoice';
import AddIdea from './pages/AddIdea';
import BusinessPlan from './pages/BusinessPlan';
import Launchpad from './pages/Launchpad';
import Marketing from './pages/Marketing';
import Finance from './pages/Finance';
import BusinessNameGenerator from './pages/marketing/BusinessNameGenerator';
import LinkedInProfileEnhancer from './pages/marketing/LinkedInProfileEnhancer';
import LinkedInBusinessProfileEnhancer from './pages/marketing/LinkedInBusinessProfileEnhancer';
import SocialPostGenerator from './pages/marketing/SocialPostGenerator';
import SocialMediaPlanner from './pages/marketing/SocialMediaPlanner';
import LogoGenerator from './pages/marketing/LogoGenerator';
import TextToImageGenerator from './pages/marketing/TextToImageGenerator';
import PressReleaseGenerator from './pages/marketing/PressReleaseGenerator';
import BlogPostCreator from './pages/marketing/BlogPostCreator';
import PitchDeck from './pages/PitchDeck';
import PriceCalculator from './pages/finance/PriceCalculator';
import ProfitMarginAnalyzer from './pages/finance/ProfitMarginAnalyzer';
import BreakEvenCalculator from './pages/finance/BreakEvenCalculator';
import CashFlowForecaster from './pages/finance/CashFlowForecaster';
import PricingStrategyAdvisor from './pages/finance/PricingStrategyAdvisor';
import SupplierFinder from './pages/finance/SupplierFinder';
import RiskManager from './pages/finance/RiskManager';
import University from './pages/University';
import EntrepreneurialMindset from './pages/EntrepreneurialMindset';
import Resilience from './pages/Resilience';
import Adaptability from './pages/Adaptability';
import Vision from './pages/Vision';
import AboutUs from './pages/AboutUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import ContactUs from './pages/ContactUs';
import DisclaimerPage from './pages/Disclaimer';
import ResetPassword from './pages/ResetPassword';
import ResetPasswordConfirm from './pages/ResetPasswordConfirm';
import AccountPage from './pages/account/AccountPage';
import AuthGuard from './components/auth/AuthGuard';
import ExplanatumVsIdeaBuddy from './pages/comparison/ExplanatumVsIdeaBuddy';
import ExplanatumVsBizplan from './pages/comparison/ExplanatumVsBizplan';
import ExplanatumVsCanvanizer from './pages/comparison/ExplanatumVsCanvanizer';
import UniversityContentManager from './pages/admin/UniversityContentManager';
import ContentDetail from './pages/university/ContentDetail';
import PaymentPage from './pages/payment/PaymentPage';
import PaymentSuccessPage from './pages/payment/PaymentSuccessPage';
import GuestCheckout from './pages/payment/GuestCheckout';
import CheckoutSuccessPage from './pages/payment/CheckoutSuccessPage';
import BusinessConsultant from './pages/BusinessConsultant';
import SignupPage from './pages/SignupPage';

// Lazy load the SocialMediaPlanner for better performance
const LazySocialMediaPlanner = lazy(() => import('./pages/marketing/SocialMediaPlanner'));

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <div className="min-h-screen bg-gray-100">
          <Navbar />
          <main className="py-10">
            <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/reset-password-confirm" element={<ResetPasswordConfirm />} />
                <Route
                  path="/idea-generator"
                  element={
                    <AuthGuard>
                      <IdeaGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/idea-choice"
                  element={
                    <AuthGuard>
                      <IdeaChoice />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/add-idea"
                  element={
                    <AuthGuard>
                      <AddIdea />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/business-plan"
                  element={
                    <AuthGuard>
                      <BusinessPlan />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/business-consultant"
                  element={
                    <AuthGuard>
                      <BusinessConsultant />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/launchpad"
                  element={
                    <AuthGuard>
                      <Launchpad />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing"
                  element={
                    <AuthGuard>
                      <Marketing />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/business-name-generator"
                  element={
                    <AuthGuard>
                      <BusinessNameGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/linkedin-profile-enhancer"
                  element={
                    <AuthGuard>
                      <LinkedInProfileEnhancer />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/social-post-generator"
                  element={
                    <AuthGuard>
                      <SocialPostGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/logo-generator"
                  element={
                    <AuthGuard>
                      <LogoGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/linkedin-business-profile-enhancer"
                  element={
                    <AuthGuard>
                      <LinkedInBusinessProfileEnhancer />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/planner"
                  element={
                    <AuthGuard>
                      <Suspense fallback={
                        <div className="flex items-center justify-center h-64">
                          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                      }>
                        <LazySocialMediaPlanner />
                      </Suspense>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/text-to-image-generator"
                  element={
                    <AuthGuard>
                      <TextToImageGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/press-release-generator"
                  element={
                    <AuthGuard>
                      <PressReleaseGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/blog-post-creator"
                  element={
                    <AuthGuard>
                      <BlogPostCreator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/social-media-planner"
                  element={
                    <AuthGuard>
                      <SocialMediaPlanner />
                    </AuthGuard>
                  }
                />
                {/* Finance Routes */}
                <Route
                  path="/finance"
                  element={
                    <AuthGuard>
                      <Finance />
                    </AuthGuard>
                  }
                />
                {/* Placeholder routes for finance tools - components to be created */}
                <Route
                  path="/finance/price-calculator"
                  element={
                    <AuthGuard>
                      <PriceCalculator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/finance/profit-margin"
                  element={
                    <AuthGuard>
                      <ProfitMarginAnalyzer />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/finance/break-even"
                  element={
                    <AuthGuard>
                      <BreakEvenCalculator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/finance/cash-flow"
                  element={
                    <AuthGuard>
                      <CashFlowForecaster />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/finance/pricing-strategy"
                  element={
                    <AuthGuard>
                      <PricingStrategyAdvisor />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/finance/supplier-finder"
                  element={
                    <AuthGuard>
                      <SupplierFinder />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/finance/risk-manager"
                  element={
                    <AuthGuard>
                      <RiskManager />
                    </AuthGuard>
                  }
                />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/disclaimer" element={<DisclaimerPage />} />
                <Route path="/contact-us" element={<ContactUs />} />
                
                {/* Payment Routes */}
                <Route path="/payment" element={<AuthGuard><PaymentPage /></AuthGuard>} />
                <Route path="/payment/success" element={<AuthGuard><PaymentSuccessPage /></AuthGuard>} />
                
                {/* Pitch Deck Route */}
                <Route
                  path="/pitch-deck"
                  element={
                    <AuthGuard>
                      <PitchDeck />
                    </AuthGuard>
                  }
                />
                
                {/* Guest Checkout Routes - No Auth Required */}
                <Route path="/checkout/guest" element={<GuestCheckout />} />
                <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                <Route path="/account" element={<AuthGuard><AccountPage /></AuthGuard>} />
                {/* Cash Flow and Pricing Strategy are marked as Coming Soon in the Finance page */}
                {/* Removed Invoice Generator and Expense Categorizer routes */}

                {/* Comparison Pages */}
                <Route path="/comparison/explanatum-vs-ideabuddy" element={<ExplanatumVsIdeaBuddy />} />
                <Route path="/comparison/explanatum-vs-bizplan" element={<ExplanatumVsBizplan />} />
                <Route path="/comparison/explanatum-vs-canvanizer" element={<ExplanatumVsCanvanizer />} />
              </Routes>
            </div>
          </main>
          <Footer /> 
          {/* Add Footer component */}
          <Toaster position="top-right" />
          <CookieConsent privacyPolicyUrl="/privacy-policy" />
        </div>
      </AuthProvider>
    </Router>
  );
};

export default App;
